import React, { useState, useEffect, useContext } from "react";
import { loadStripe } from "@stripe/stripe-js";

import "./style.scss";
import { base_url, stripe_key } from "../../config";
import RegistrationContext from "../../contexts/RegistrationContext";
import axios from "axios";
import AlertDialog from "../../components/AlertDialog/AlertDialog";
import RegistrationSummary from "../../components/RegistrationSummary/RegistrationSummary";

// Make sure to call loadStripe outside of a component’s render to avoid
// recreating the Stripe object on every render.
// This is your test publishable API key.
const stripePromise = loadStripe(stripe_key);

export default function App() {
  const { data, setError, setCart, cart, setReload, reload } =
    useContext(RegistrationContext);
  const [, setClientSecret] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscountCode, setAppliedDiscountCode] = useState(null);
  const [, setIsLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState("");

  const [discountStatus, setDiscountStatus] = useState("idle"); // "idle" | "loading" | "success" | "error"

  const [discountMessage, setDiscountMessage] = useState("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const handleApplyCode = async () => {
    try {
      const response = await axios.post(`${base_url}/api/apply-code`, {
        code: discountCode,
      });
      setAppliedDiscountCode(discountCode);

      setDiscountStatus("success");
      setDiscountMessage("Discount code applied successfully!");
      setCart(response.data.cart);
    } catch (error) {
      setAppliedDiscountCode(null);
      setDiscountStatus("error");
      setDiscountMessage(error.response.data.message);
      if (error.response.data.cart) {
        setCart(error.response.data.cart);
      }
    }
  };

  useEffect(() => {
    setIsLoading(true);

    if (cart.total_price === 0) {
      setClientSecret("free");
      setIsLoading(false);
      return;
    }

    // Create PaymentIntent as soon as the page loads
    axios
      .post(`${base_url}/api/checkout`, { items: [{ id: `Reg. ${data.id}` }] })
      .then((res) => {
        setIsLoading(false);
        setClientSecret(res.data.clientSecret);
      })
      .catch((err) => {
        setIsLoading(false);
      });
  }, [cart, data.id]);

  const handleProceedToPayment = async () => {
    setIsProcessingPayment(true);

    try {
      const response = await axios.post(
        `${base_url}/api/create-checkout-session`,
        {
          amount: cart.total_price, // Pass the total price to the backend
          code: appliedDiscountCode,
        }
      );

      const stripe = await stripePromise;
      await stripe.redirectToCheckout({ sessionId: response.data.id });
    } catch (error) {
      setError("Failed to initiate payment. Please try again.");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleProceedFree = async () => {
    setIsProcessingPayment(true);

    try {
      await axios.post(`${base_url}/api/free-registration`, {
        code: appliedDiscountCode,
      });

      setReload(reload + 1);
    } catch (error) {
      setError("Failed to initiate payment. Please try again.");
    } finally {
      setIsProcessingPayment(false);
    }
  };
  const closeDialog = () => {
    setAlertMessage("");
  };
  return (
    <>
      {alertMessage && (
        <AlertDialog setOpen={closeDialog} title="ECAI Registration">
          {alertMessage}
        </AlertDialog>
      )}
      <div className="container mx-auto px-4 py-8 flex flex-col overflow-y-scroll h-full flex-grow-1">
        <div className="bg-white rounded-xl shadow-lg flex flex-col flex-1 py-6">
          <div className="w-full max-w-6xl mx-auto bg-white p-4 rounded-lg flex flex-col justify-between h-full">
            <RegistrationSummary expanded />

            {/* Discount Code Section - Redesigned */}
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "24px",
                marginBottom: "24px",
                backgroundColor: "#fff",
              }}
            >
              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "16px",
                }}
              >
                Discount Code
              </h2>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <input
                  type="text"
                  placeholder="Enter your discount code"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  disabled={discountStatus === "loading"}
                  style={{
                    flex: "1",
                    padding: "8px 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
                <button
                  onClick={handleApplyCode}
                  disabled={
                    !discountCode.trim() || discountStatus === "loading"
                  }
                  style={{
                    minWidth: "100px",
                    padding: "8px 16px",
                    backgroundColor:
                      discountStatus === "success" ? "#16a34a" : "#4361ee",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor:
                      discountStatus === "loading" ||
                      discountStatus === "success"
                        ? "pointer"
                        : "pointer",
                    opacity:
                      !discountCode.trim() ||
                      discountStatus === "loading" ||
                      discountStatus === "success"
                        ? 0.7
                        : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  {discountStatus === "loading"
                    ? "Applying..."
                    : discountStatus === "success"
                    ? "Applied"
                    : "Apply Code"}
                </button>
              </div>

              {discountStatus === "success" && (
                <div
                  style={{
                    backgroundColor: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    color: "#166534",
                    padding: "12px",
                    borderRadius: "4px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span style={{ marginRight: "8px" }}>✓</span>
                  {discountMessage}
                </div>
              )}

              {discountStatus === "error" && (
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#b91c1c",
                    padding: "12px",
                    borderRadius: "4px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span style={{ marginRight: "8px" }}>⚠</span>
                  {discountMessage}
                </div>
              )}
            </div>

            {cart.total_price === 0 && (appliedDiscountCode || cart.ppi_discount) ? (
              <button
                onClick={handleProceedFree}
                disabled={isProcessingPayment}
                style={{
                  width: "100%",
                  padding: "16px",
                  backgroundColor: "#4361ee",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "18px",
                  fontWeight: "500",
                  cursor: isProcessingPayment ? "not-allowed" : "pointer",
                  opacity: isProcessingPayment ? 0.7 : 1,
                }}
              >
                {isProcessingPayment
                  ? "Processing..."
                  : "Registration is free, click here to proceed"}
              </button>
            ) : (
              <>
                <button
                  onClick={handleProceedToPayment}
                  disabled={isProcessingPayment || cart.total_price === 0}
                  style={{
                    width: "100%",
                    padding: "16px",
                    backgroundColor: "#4361ee",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    fontSize: "18px",
                    fontWeight: "500",
                    cursor:
                      isProcessingPayment || cart.total_price === 0
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      isProcessingPayment || cart.total_price === 0 ? 0.7 : 1,
                  }}
                >
                  {isProcessingPayment ? "Processing..." : "Proceed to Payment"}
                </button>
                {/* Additional Information */}
                <div
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    marginBottom: "24px",
                  }}
                >
                  <p style={{ marginBottom: "8px" }}>
                    You will be redirected to our secure payment gateway to
                    complete your registration.
                  </p>
                </div>
              </>
            )}

            {/* <div className="flex  gap-4">
              {/* Discount code form * /}
              <div className="discountForm-wrapper flex justify-center">
                <div className="discountForm flex justify-between items-center gap-4 flex-col lg:flex-row lg:gap-0">
                  <div>Have discount code?</div>

                  <form>
                    <input
                      placeholder="Introduce your code"
                      className="discountInput"
                      onChange={e => setDiscountCode(e.target.value)}
                      value={discountCode}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleApplyCode();
                        }
                      }}
                      disabled={codeApplied}

                    ></input>
                  </form>
                  {codeApplied ? (
                    <button onClick={() => removeCode()} className="buttonDiscount">
                      <Trash2 />
                    </button>
                  ) : (
                    <button onClick={handleApplyCode} className="buttonDiscount">
                      Apply
                    </button>
                  )}

                </div>
              </div>

              {/* Payment form 
              <div>
                {clientSecret &&
                  !loading &&
                  (cart.total_price > 0 ? (
                    <>
                      {1 && (
                        <div className="payment">
                          <Elements options={options} stripe={stripePromise}>
                            <CheckoutForm />
                          </Elements>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="">
                      <FreePayment />
                    </div>
                  ))}
              </div>
              * /}
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
}
